/**
 * Kontekstuelt egenskaps-panel (høyre side).
 * Vises når en reol, flere reoler eller en enkeltlokasjon er valgt.
 * Redigerer posisjon (med grid-snap), rotasjon, farge og type via
 * eksisterende reducer-actions. Skjules når bulk-plasseringspanelet er åpent.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  MapPin,
  Trash2,
  RotateCcw,
  AlignStartVertical,
  X,
  Anchor,
  Ban,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { snapToGrid } from '../utils/positionCalculator';
import { resolveBinRenderInfo } from '../utils/layoutApiMapping';
import type { ServerBinPosition } from '../../../../../services/warehouseLayoutService';

/** Fargepalett for reoler — datakonstant (lagres på reolen), matcher modulens eksisterende fargebruk */
const RACK_COLOR_PALETTE = [
  '#3b82f6', // blå (standard-tone)
  '#22c55e', // grønn
  '#f59e0b', // oransje
  '#ef4444', // rød
  '#8b5cf6', // lilla
  '#06b6d4', // cyan
  '#ec4899', // rosa
  '#64748b', // grå
];

const ROTATIONS: Array<0 | 90 | 180 | 270> = [0, 90, 180, 270];

/** Etikett for seksjoner i panelet */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
    {children}
  </label>
);

/**
 * Tallfelt med lokal kladd — committer på blur/Enter slik at grid-snap
 * ikke slåss med brukeren midt i tastingen.
 */
const NumberField: React.FC<{
  label: string;
  value: number;
  step: number;
  onCommit: (value: number) => void;
}> = ({ label, value, step, onCommit }) => {
  const [draft, setDraft] = useState(value.toFixed(1));

  // Synkroniser kladden når verdien endres utenfra (drag i canvas, undo, etc.)
  useEffect(() => {
    setDraft(value.toFixed(1));
  }, [value]);

  const commit = () => {
    const parsed = Number.parseFloat(draft.replace(',', '.'));
    if (Number.isFinite(parsed)) {
      onCommit(parsed);
    } else {
      setDraft(value.toFixed(1));
    }
  };

  return (
    <div>
      <span className="block text-xs text-gray-500 mb-0.5">{label}</span>
      <input
        type="number"
        step={step}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );
};

/**
 * Tekstfelt med lokal kladd — committer på blur/Enter (samme mønster som NumberField).
 * Tom streng committes som null (tilbake til standardverdien).
 */
const TextField: React.FC<{
  value: string;
  placeholder: string;
  onCommit: (value: string | null) => void;
}> = ({ value, placeholder, onCommit }) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed !== value) onCommit(trimmed || null);
  };

  return (
    <input
      type="text"
      value={draft}
      placeholder={placeholder}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); }}
      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    />
  );
};

/** Segmenterte rotasjonsknapper (0/90/180/270) */
const RotationButtons: React.FC<{
  value: number;
  onChange: (rotation: 0 | 90 | 180 | 270) => void;
}> = ({ value, onChange }) => (
  <div className="grid grid-cols-4 gap-1">
    {ROTATIONS.map(deg => (
      <button
        key={deg}
        onClick={() => onChange(deg)}
        className={`py-1.5 text-xs font-medium rounded-md border transition-colors ${
          value === deg
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
        }`}
      >
        {deg}&deg;
      </button>
    ))}
  </div>
);

// ============================================
// Enkelt-reol
// ============================================

const RackProperties: React.FC<{ rackNumber: string }> = ({ rackNumber }) => {
  const { state, dispatch, rackDimensionsMap } = useLayoutEditor();
  const { t } = useTranslation();
  const layout = state.layout!;
  const rack = layout.racks.find(r => r.rackNumber === rackNumber);
  const dim = rackDimensionsMap.get(rackNumber);
  const isAnchor = state.editorState.anchorRack === rackNumber;

  if (!rack) return null;

  const commitPosition = (field: 'x' | 'y', value: number) => {
    const snapped = snapToGrid(value, layout.gridUnit);
    dispatch({
      type: 'SET_RACK_POSITION',
      rackNumber,
      x: field === 'x' ? snapped : rack.x,
      y: field === 'y' ? snapped : rack.y,
    });
  };

  return (
    <div className="p-4 space-y-5">
      {/* Mål (skrivebeskyttet — kommer fra StcBinSize/heuristikk) */}
      {dim && (
        <div>
          <FieldLabel>{t('manager.modules.warehouseLayout.properties.size', {}, 'Mål (B × D × H)')}</FieldLabel>
          <p className="text-sm text-gray-700 font-mono">
            {dim.width.toFixed(1)}m &times; {dim.depth.toFixed(1)}m &times; {dim.height.toFixed(1)}m
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {dim.verticals}&times;{dim.shelves} {t('manager.modules.warehouseLayout.properties.grid', {}, 'plasser')}
          </p>
        </div>
      )}

      {/* Posisjon */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.position', {}, 'Posisjon')}</FieldLabel>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={t('manager.modules.warehouseLayout.properties.posX', {}, 'X (m)')}
            value={rack.x}
            step={layout.gridUnit}
            onCommit={v => commitPosition('x', v)}
          />
          <NumberField
            label={t('manager.modules.warehouseLayout.properties.posY', {}, 'Y (m)')}
            value={rack.y}
            step={layout.gridUnit}
            onCommit={v => commitPosition('y', v)}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">
          {t('manager.modules.warehouseLayout.properties.gridSnapHint', { '0': layout.gridUnit }, `Festes til rutenett (${layout.gridUnit} m)`)}
        </p>
      </div>

      {/* Rotasjon */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.rotation', {}, 'Rotasjon')}</FieldLabel>
        <RotationButtons
          value={rack.rotation}
          onChange={rotation => dispatch({ type: 'SET_RACK_ROTATION', rackNumber, rotation })}
        />
      </div>

      {/* Etikett (vises på reolen i 2D når «Etiketter» er på) */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.label', {}, 'Etikett')}</FieldLabel>
        <TextField
          key={rackNumber}
          value={rack.label ?? ''}
          placeholder={rackNumber}
          onCommit={label => dispatch({ type: 'SET_RACK_LABEL', rackNumber, label })}
        />
        <p className="text-xs text-gray-400 mt-1">
          {t('manager.modules.warehouseLayout.properties.labelHint', {}, 'Tom = reolnummeret brukes')}
        </p>
      </div>

      {/* Farge */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.color', {}, 'Farge')}</FieldLabel>
        <div className="flex flex-wrap gap-1.5 items-center">
          {RACK_COLOR_PALETTE.map(color => (
            <button
              key={color}
              onClick={() => dispatch({ type: 'SET_RACK_COLOR', rackNumber, color })}
              className={`w-7 h-7 rounded-md border transition-transform hover:scale-110 ${
                rack.color === color ? 'ring-2 ring-offset-1 ring-blue-500 border-transparent' : 'border-gray-300'
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
          <button
            onClick={() => dispatch({ type: 'SET_RACK_COLOR', rackNumber, color: null })}
            className={`w-7 h-7 rounded-md border flex items-center justify-center text-gray-400 hover:text-gray-600 transition-transform hover:scale-110 ${
              !rack.color ? 'ring-2 ring-offset-1 ring-blue-500 border-transparent bg-gray-50' : 'border-gray-300 bg-white'
            }`}
            title={t('manager.modules.warehouseLayout.properties.resetColor', {}, 'Standardfarge')}
          >
            <Ban className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Handlinger */}
      <div className="pt-3 border-t border-gray-200 space-y-2">
        <button
          onClick={() => dispatch({ type: 'SET_ANCHOR', rackNumber: isAnchor ? null : rackNumber })}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
            isAnchor
              ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Anchor className="w-4 h-4" />
          {isAnchor
            ? t('manager.modules.warehouseLayout.placedRacks.removeAsAnchor', {}, 'Fjern som anker')
            : t('manager.modules.warehouseLayout.placedRacks.setAsAnchor', {}, 'Sett som anker')}
        </button>
        <button
          onClick={() => dispatch({ type: 'REMOVE_RACK', rackNumber })}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          {t('manager.modules.warehouseLayout.properties.removeRack', {}, 'Fjern fra layout')}
        </button>
      </div>
    </div>
  );
};

// ============================================
// Flervalg (reoler)
// ============================================

const MultiRackProperties: React.FC = () => {
  const { dispatch } = useLayoutEditor();
  const { t } = useTranslation();

  return (
    <div className="p-4 space-y-3">
      <p className="text-xs text-gray-500">
        {t('manager.modules.warehouseLayout.properties.multiHint', {}, 'Handlingene under gjelder alle valgte reoler.')}
      </p>
      <button
        onClick={() => dispatch({ type: 'ROTATE_SELECTED' })}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <RotateCcw className="w-4 h-4" />
        {t('manager.modules.warehouseLayout.properties.rotateAll', {}, 'Roter alle (R)')}
      </button>
      <button
        onClick={() => dispatch({ type: 'ALIGN_SELECTED_TO_GRID' })}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <AlignStartVertical className="w-4 h-4" />
        {t('manager.modules.warehouseLayout.properties.alignAll', {}, 'Juster til rutenett (G)')}
      </button>
      <button
        onClick={() => dispatch({ type: 'DELETE_SELECTED' })}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
        {t('manager.modules.warehouseLayout.properties.deleteAll', {}, 'Slett valgte (Delete)')}
      </button>
    </div>
  );
};

// ============================================
// Enkeltlokasjon
// ============================================

type PlacementKind = ServerBinPosition['placementKind'];

const BinProperties: React.FC<{ binCode: string }> = ({ binCode }) => {
  const { state, dispatch } = useLayoutEditor();
  const { t } = useTranslation();
  const layout = state.layout!;
  const position = state.binPositions.find(p => p.binCode === binCode);

  const specialByCode = useMemo(
    () => new Map(state.specialBins.map(b => [b.binCode, b])),
    [state.specialBins]
  );

  if (!position) return null;

  const info = resolveBinRenderInfo(position, specialByCode);

  const kindOptions: Array<{ value: PlacementKind; label: string }> = [
    { value: 'FLOOR', label: t('manager.modules.warehouseLayout.unplacedBins.kindFloor', {}, 'Gulv') },
    { value: 'WALL', label: t('manager.modules.warehouseLayout.unplacedBins.kindWall', {}, 'Vegg') },
    { value: 'HANGING', label: t('manager.modules.warehouseLayout.unplacedBins.kindHanging', {}, 'Heng') },
    { value: 'CABINET', label: t('manager.modules.warehouseLayout.unplacedBins.kindCabinet', {}, 'Skap') },
    { value: 'DOCK', label: t('manager.modules.warehouseLayout.unplacedBins.kindDock', {}, 'Dock') },
  ];

  const commitPosition = (field: 'x' | 'y', value: number) => {
    const snapped = snapToGrid(value, layout.gridUnit);
    dispatch({
      type: 'MOVE_BIN',
      binCode,
      x: field === 'x' ? snapped : position.positionX,
      y: field === 'y' ? snapped : position.positionY,
    });
  };

  // Vis nærmeste 90-graders trinn som aktiv rotasjonsknapp
  const snappedRotation = ((Math.round(position.rotation / 90) * 90) % 360) as 0 | 90 | 180 | 270;

  return (
    <div className="p-4 space-y-5">
      {info.description && (
        <p className="text-sm text-gray-600 -mt-1">{info.description}</p>
      )}

      {/* Type */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.type', {}, 'Type')}</FieldLabel>
        <select
          value={position.placementKind}
          onChange={e => dispatch({ type: 'SET_BIN_KIND', binCode, kind: e.target.value as PlacementKind })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {kindOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Mål */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.size', {}, 'Mål (B × D × H)')}</FieldLabel>
        <p className="text-sm text-gray-700 font-mono">
          {info.widthM.toFixed(1)}m &times; {info.depthM.toFixed(1)}m &times; {info.heightM.toFixed(1)}m
        </p>
      </div>

      {/* Posisjon */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.position', {}, 'Posisjon')}</FieldLabel>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label={t('manager.modules.warehouseLayout.properties.posX', {}, 'X (m)')}
            value={position.positionX}
            step={layout.gridUnit}
            onCommit={v => commitPosition('x', v)}
          />
          <NumberField
            label={t('manager.modules.warehouseLayout.properties.posY', {}, 'Y (m)')}
            value={position.positionY}
            step={layout.gridUnit}
            onCommit={v => commitPosition('y', v)}
          />
        </div>
      </div>

      {/* Rotasjon */}
      <div>
        <FieldLabel>{t('manager.modules.warehouseLayout.properties.rotation', {}, 'Rotasjon')}</FieldLabel>
        <RotationButtons
          value={snappedRotation}
          onChange={rotation => dispatch({ type: 'SET_BIN_ROTATION', binCode, rotation })}
        />
      </div>

      {/* Fjern plassering (lokasjonen beholdes — kun posisjonen fjernes) */}
      <div className="pt-3 border-t border-gray-200">
        <button
          onClick={() => dispatch({ type: 'REMOVE_BIN', binCode })}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          {t('manager.modules.warehouseLayout.properties.removeBin', {}, 'Fjern plassering')}
        </button>
      </div>
    </div>
  );
};

// ============================================
// Panel-skall
// ============================================

const PropertiesPanel: React.FC = () => {
  const { state, dispatch } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, editorState, selectedBinCode, showPlacementPanel } = state;

  // Bulk-plasseringspanelet har forrang på høyresiden
  if (!layout || showPlacementPanel) return null;

  const selectedCount = editorState.selectedRacks.size;
  const singleRack = selectedCount === 1 ? Array.from(editorState.selectedRacks)[0] : null;

  if (selectedCount === 0 && !selectedBinCode) return null;

  const title = selectedBinCode
    ? t('manager.modules.warehouseLayout.properties.binTitle', { '0': selectedBinCode }, `Lokasjon ${selectedBinCode}`)
    : singleRack
      ? t('manager.modules.warehouseLayout.properties.rackTitle', { '0': singleRack }, `Reol ${singleRack}`)
      : t('manager.modules.warehouseLayout.properties.multiTitle', { '0': selectedCount }, `${selectedCount} reoler valgt`);

  const handleClose = () => {
    if (selectedBinCode) {
      dispatch({ type: 'SET_SELECTED_BIN', binCode: null });
    } else {
      dispatch({ type: 'SET_SELECTION', selectedRacks: new Set<string>() });
    }
  };

  return (
    <div className="w-72 bg-white border-l border-gray-200 flex flex-col h-full flex-shrink-0 overflow-hidden">
      {/* Panel-header */}
      <div className="flex items-center gap-2 p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        {selectedBinCode
          ? <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
          : <Box className="w-5 h-5 text-blue-600 flex-shrink-0" />}
        <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
        <button
          onClick={handleClose}
          className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title={t('common.buttons.close', {}, 'Lukk')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Innhold */}
      <div className="flex-1 overflow-y-auto">
        {selectedBinCode ? (
          <BinProperties binCode={selectedBinCode} />
        ) : singleRack ? (
          <RackProperties rackNumber={singleRack} />
        ) : (
          <MultiRackProperties />
        )}
      </div>
    </div>
  );
};

export default PropertiesPanel;
